import React, { useContext } from "react";

import { Preview } from "@creatomate/preview";
import { Button, Icon, PlainButton, Tooltip } from "@culturehq/components";

import { Trans } from "app/utils";

import { useTemplateBuilder } from "components/admin/templates/context/TemplateBuilderContext";
import { getCreatomateMediaData } from "../../utils/creatomate/utils";
import { CreatomateContext } from "../../contexts/VideoStudioContext";

interface TemplateActionButtonsProps {
  preview: Preview;
  onClose: () => void;
}

const ActiveStateBtn: React.FC<TemplateActionButtonsProps> = ({ preview, onClose }) => {
  const { state: { template }, handleSave, restartContextValues } = useTemplateBuilder();
  const { data, restartData } = useContext(CreatomateContext);

  const active = template?.status === "active";
  const buttonText = active ? "Save Changes" : "Set Active";

  const { uploadingMedia, loading } = data;
  const { ready } = preview;
  const isDisabled = loading || !ready || uploadingMedia;

  const handleSaveEdition = async (): Promise<void> => {
    await handleSave({
      status: "active",
      creatomateJson: getCreatomateMediaData(preview, false)
    });
    restartData();
    restartContextValues();
    preview.dispose();
    onClose();
  };

  const ActiveStateButton = (
    <Button disabled={isDisabled} onClick={handleSaveEdition}>
      <Icon icon={active ? "checkmark" : "play-filled"} />
      <Trans>{buttonText}</Trans>
    </Button>
  );

  if (uploadingMedia) {
    return (
      <Tooltip tip={
        <p className="Tooltip-uploaded">
          <Trans>The video is being uploaded</Trans>
        </p> as unknown as string
      }
      >
        {ActiveStateButton}
      </Tooltip>
    );
  }

  return ActiveStateButton;
};

const DraftStateBtn: React.FC<TemplateActionButtonsProps> = ({ preview, onClose }) => {
  const { state: { template }, handleSave, restartContextValues } = useTemplateBuilder();
  const { data, restartData } = useContext(CreatomateContext);

  const active = template?.status === "active";
  const buttonText = active ? "Pause" : "Save as Draft";

  const { uploadingMedia, loading } = data;
  const { ready } = preview;
  const isDisabled = loading || !ready || uploadingMedia;

  const handleSaveEdition = async (): Promise<void> => {
    // we load the source because we do not need to add multipart values for a draft template
    await handleSave({
      status: "draft",
      creatomateJson: preview.getSource()
    });
    restartData();
    restartContextValues();
    preview.dispose();
    onClose();
  };

  const DraftStateButton = (
    <PlainButton
      className="Download-Btn"
      disabled={isDisabled}
      onClick={handleSaveEdition}
    >
      <Icon icon={active ? "pause" : "checkmark"} />
      <Trans>{buttonText}</Trans>
    </PlainButton>
  );

  if (uploadingMedia) {
    return (
      <Tooltip tip={
        <p className="Tooltip-uploaded">
          <Trans>The video is being uploaded</Trans>
        </p> as unknown as string
      }
      >
        {DraftStateButton}
      </Tooltip>
    );
  }

  return DraftStateButton;
};

const TemplateActionButtons: React.FC<TemplateActionButtonsProps> = ({
  preview,
  onClose
}) => (
  <>
    <DraftStateBtn preview={preview} onClose={onClose} />
    <ActiveStateBtn preview={preview} onClose={onClose} />
  </>
);

export default TemplateActionButtons;
